import { Check, Filter, Flag, Pause, Star, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MARKS } from "@/config/filterFields";
import { cn } from "@/lib/utils";
import type { ClicksMark } from "@/services/ClicksService";

const STYLE: Record<string, { Icon: typeof Flag; tone: string }> = {
  suspicious: { Icon: Flag, tone: "text-destructive dark:text-red-400" },
  watch: { Icon: Star, tone: "text-amber-500" },
  pause: { Icon: Pause, tone: "text-sky-600 dark:text-sky-400" },
};

/** A mark's icon in its colour; nothing for an unknown mark. */
export function MarkIcon({ mark, className }: { mark: string; className?: string }) {
  const s = STYLE[mark];
  if (!s) return null;
  return <s.Icon className={cn("size-3.5 shrink-0", s.tone, className)} fill="currentColor" aria-hidden />;
}

/** What was clicked: a row's mark button (focus goes back to it). Null closes the menu. */
export type MarkTarget = { x: number; y: number; value: string; mark: ClicksMark | null; returnFocus?: HTMLElement | null };

const label = (mark: string) => MARKS.find((m) => m.value === mark)?.label ?? mark;
const when = (at: string) => new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

/**
 * The marks of one table value (20261030183128): set, change or remove it -
 * for everyone - and show only the rows with that mark. One menu for the whole
 * table, opened at the button, like the right-click menu: a Radix root per row
 * would mount hundreds.
 */
export function MarkMenu({ target, onClose, onSet, onShowOnly }: {
  target: MarkTarget | null;
  onClose: () => void;
  onSet: (value: string, mark: string | null) => void;
  onShowOnly: (mark: string) => void;
}) {
  if (!target) return null;
  const cur = target.mark;
  return (
    <DropdownMenu open onOpenChange={(o) => !o && onClose()}>
      <DropdownMenuTrigger
        aria-hidden
        tabIndex={-1}
        className="pointer-events-none fixed size-0 p-0"
        style={{ left: target.x, top: target.y }}
      />
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground" title={target.value}>
          {cur ? `${label(cur.mark)} · ${cur.by ?? "someone"} · ${when(cur.at)}` : `Mark ${target.value}`}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {MARKS.map((m) => (
          <DropdownMenuItem key={m.value} onSelect={() => onSet(target.value, m.value)}>
            <MarkIcon mark={m.value} className="size-4" />
            {m.label}
            {cur?.mark === m.value ? <Check className="ml-auto size-4" aria-label="current" /> : null}
          </DropdownMenuItem>
        ))}
        {cur ? (
          <>
            <DropdownMenuItem onSelect={() => onSet(target.value, null)}>
              <X className="size-4" />
              Remove mark
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onShowOnly(cur.mark)}>
              <Filter className="size-4" />
              Show only “{label(cur.mark)}” rows
            </DropdownMenuItem>
          </>
        ) : null}
        <p className="px-2 pt-1 pb-1.5 text-[11px] text-muted-foreground">Marks are shared: everyone sees them.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
